/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import {
  Text,
  withSitecoreContext,
  Placeholder,
} from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import throttle from 'lodash/throttle';
import includes from 'lodash/includes';
import get from 'lodash/get';
import map from 'lodash/map';
import compose from 'lodash/fp/compose';
import defaultsDeep from 'lodash/defaultsDeep';
import getComponent from '../../../utils/getComponent';
import { getPrefilledSolution } from '../../../utils/getPrefilledSolutionValue';
import { hideElementCss, showElementCss } from '../../../utils/responsive';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { SOLUTIONS_DICTIONARIES as dict } from '../../../utils/dictionarykeys';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { processLinkData } from '../../../utils/analytics';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import DotComponent from '../../ui/DotComponent';
import OpenEnquiryFormButton from '../../ui/OpenEnquiryFormButton';
import Picture from '../../ui/Picture';
import { Select } from '../../ui/Form';
import { mapDropdownValues } from '../../ui/Form/Dropdown';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import LinkWrapper from '../../ui/LinkWrapper';
import applyPluralRule from '../../../utils/pluralRule';
import { StickyBarConsumer } from '../../../contexts/StickyBarContext';
import { withConsumer } from '../../../contexts';
import {
  Container,
  Row,
  Column,
  Bleed,
  MediaQueries,
} from '../../../styles/Grid';
import * as styles from './index.styles';

const variationDualCta = 'DualCTA';
const variationSingleCta = 'SingleCTA';
const variationDropdowns = 'DualInputPlusCTA';

const mainImageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '4:3',
    width: 906,
    height: 679,
    gravity: 'auto:subject',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 480,
    height: 360,
    gravity: 'auto:subject',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 983,
    height: 768,
    gravity: 'auto:subject',
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 766,
    height: 598,
    gravity: 'auto:subject',
    aspectRatio: '4:3',
  },
};

const leftImageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 499,
    height: 315,
    gravity: 'auto:subject',
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 499,
    height: 315,
    gravity: 'auto:subject',
  },
  [Breakpoints.md]: {
    hidden: true,
  },
  [Breakpoints.sm]: {
    hidden: true,
  },
};

const defaultData = {
  Headline: {
    value: '',
  },
  NumberOfNearbyLocations: 0,
  Subtitle: [],
  HeroImage1: {
    value: {
      src: '',
      alt: '',
    },
  },
  HeroImage2: {
    value: {
      src: '',
      alt: '',
    },
  },
  Description: {
    value: '',
  },
  PhoneNumber: {
    value: '',
  },
  HowManyPeopleOptions: [],
  MoveInDateOptions: [],
  GetInTouchCTA: {
    value: {
      href: '',
      linktype: '',
      text: '',
      anchor: '',
      querystring: '',
      target: '',
    },
  },
};

export class GeosolutionHeader extends SafePureComponent {
  stickyBarTriggerRef = React.createRef();

  constructor(props) {
    super(props);

    this.fields = defaultsDeep(this.props.rendering.fields, defaultData);
    this.state = {
      pageContainsSolutionsCards: false,
    };

    this.howManyPeopleSelect = React.createRef();
    this.moveInDateSelect = React.createRef();
    this.wildcards = getWildcardData(this.props.sitecoreContext);
  }

  componentDidMount() {
    const { sitecoreContext } = this.props;
    this.setState({
      pageContainsSolutionsCards: Boolean(
        getComponent(sitecoreContext, 'SolutionsCards'),
      ),
    });

    window.addEventListener('scroll', this.onScrollHandler);
    this.setStickyBarLayout();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScrollHandler = throttle(() => {
    const { stickyBarContext } = this.props;
    const rect = this.stickyBarTriggerRef.current.getBoundingClientRect();
    const bottom = rect.bottom;

    if (bottom < 0) {
      stickyBarContext.showStickyBar();
    } else {
      stickyBarContext.hideStickyBar();
    }
  }, 80);

  setStickyBarLayout() {
    const { stickyBarContext } = this.props;
    const headerVariation = get(this.fields, 'HeaderVariation.value');
    const hasOneButton = includes(
      [variationSingleCta, variationDropdowns],
      headerVariation,
    );

    if (hasOneButton) {
      stickyBarContext.activateOneButtonLayout();
    }
  }

  /**
   * This function is rendering the Headline of the component and transforming the wildcards values to be used inside the Headline
   * @param  {Object} fields dictionary with the component values from sitecore
   * @return {React.Node} Node with the Headline of the component
   */
  renderHeadline = fields => {
    const { t } = this.props;
    const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
    const componentName = get(this.props, 'rendering.componentName');
    const Headline = {
      value: transformWildcardsToText({
        componentName,
        contextFields,
        fields,
        text: get(fields, 'Headline.value', ''),
        wildcards: this.wildcards,
        t,
      }),
    };

    return (
      <h1 css={styles.title}>
        <Text field={Headline} />
        <DotComponent />
      </h1>
    );
  };

  renderContent(fields) {
    const shouldShowPhoneNumber = !get(fields, 'HidePhoneNumber.value', false);
    const locationsCta = get(fields, 'LocationsLinkCTA.value', '');
    const geoSolutionLabel = applyPluralRule({
      quantity: fields.NumberOfNearbyLocations,
      dictionary: 'GeoSolutionLocationLabel',
    });
    const componentName = get(this.props, 'rendering.componentName');
    const isGTMTrackable = true;
    const fieldCta = {
      value: {
        href: locationsCta,
        text: `${fields.NumberOfNearbyLocations} ${geoSolutionLabel}`,
      },
    };

    return (
      <React.Fragment>
        <Column cols={{ lg: 5 }} offsets={{ lg: 1 }}>
          <div css={styles.content}>
            {this.renderHeadline(fields)}
            <div css={styles.locationsSubtitle}>
              <a
                css={styles.locationsCtaAnchor}
                onClick={() =>
                  processLinkData(componentName, fieldCta, isGTMTrackable)
                }
                href={locationsCta}
              >
                <span css={styles.locationsIndicator}>
                  {fields.NumberOfNearbyLocations}
                </span>
                <span
                  className="locations-cta-text"
                  css={[styles.subtitle, styles.locationsCtaLink]}
                >
                  {geoSolutionLabel}
                </span>
              </a>
              <span css={[styles.separator, styles.locationSeparator]}>/</span>
              <span css={[styles.subtitle, styles.secondSubtitle]}>
                {map(fields.Subtitle, (field, index) => (
                  <span key={`subtitle-${index}`}>
                    {index > 0 && <span css={styles.separator}>/</span>}
                    <Text
                      field={get(field, 'fields.Label', {})}
                      editable={false}
                    />
                  </span>
                ))}
              </span>
            </div>
            <div css={styles.description}>
              <Text field={fields.Description} />
            </div>
            {shouldShowPhoneNumber && (
              <div css={styles.phoneNumber}>
                <Placeholder
                  name="jss-phone"
                  rendering={this.props.rendering}
                />
              </div>
            )}
            {this.renderForm(fields)}
          </div>
        </Column>

        <Column css={showElementCss(MediaQueries.lgAndUp)} cols={{ lg: 6 }}>
          <Bleed css={styles.imageContainerBleed} right>
            {this.renderMainImage(fields)}
          </Bleed>
        </Column>
      </React.Fragment>
    );
  }

  renderDropdowns(fields) {
    const { HowManyPeopleOptions, MoveInDateOptions } = fields;

    return (
      <div css={styles.dropdownsContainer}>
        <Select
          ref={this.howManyPeopleSelect}
          css={styles.dropdown}
          customLabelCss={styles.dropdownLabel}
          labelCss={styles.dropdownLabel}
          options={mapDropdownValues(HowManyPeopleOptions)}
        />
        <Select
          ref={this.moveInDateSelect}
          css={styles.dropdown}
          customLabelCss={styles.dropdownLabel}
          labelCss={styles.dropdownLabel}
          options={mapDropdownValues(MoveInDateOptions)}
        />
      </div>
    );
  }

  // TODO: getEnquiryDefaultValues was duplicated on StickyBar in order to finish
  // this asap for demo but this should be moved to a global util in order to DRY.
  getEnquiryDefaultValues = (fields, prefilledSolution) => {
    const { t } = this.props;
    return {
      Location: get(fields, 'GeoName.value', ''),
      HowManyPeople: get(
        this.howManyPeopleSelect,
        'current.state.selectedLabel',
        '',
      ),
      MoveInDate: get(this.moveInDateSelect, 'current.state.selectedLabel', ''),
      Solution: get(
        prefilledSolution,
        'fields.Label.value',
        t(dict.OFFICE_SPACE),
      ),
    };
  };

  renderGetInTouchCTA(componentName) {
    const prefilledSolution = getPrefilledSolution(this.props.sitecoreContext);
    const openEnquiryFormButtonProps = {
      linkObject: this.fields.GetInTouchCTA,
      prefilledValues: this.getEnquiryDefaultValues(
        this.fields,
        prefilledSolution,
      ),
      customCss: styles.primaryButton,
    };

    return (
      <OpenEnquiryFormButton
        {...openEnquiryFormButtonProps}
        componentName={componentName}
      />
    );
  }

  renderForm(fields) {
    const { t } = this.props;
    const variation = get(fields, 'HeaderVariation.value', variationDualCta);
    const shouldRenderSeeLocationsCta = variation === variationDualCta;
    const shouldApplyLongContainer = variation !== variationDualCta;
    const columnLgSize = shouldApplyLongContainer ? [5, 5] : [3, 5];
    const columnXlSize = shouldApplyLongContainer ? [4, 5] : [3, 5];
    const shouldRenderDropdowns = variation === variationDropdowns;
    const linkField = {
      value: {
        href: get(fields, 'SeeLocationsCTA.value'),
      },
    };
    const componentName = get(this.props, 'rendering.componentName', '');

    return (
      <Row ref={this.stickyBarTriggerRef}>
        <Column cols={{ sm: 6, lg: columnLgSize, xl: columnXlSize }}>
          <div css={styles.formContainer}>
            {shouldRenderDropdowns && this.renderDropdowns(fields)}
            {this.renderGetInTouchCTA(componentName)}
            {shouldRenderSeeLocationsCta && (
              <LinkWrapper
                field={linkField}
                type="secondary"
                componentName={componentName}
              >
                {t('SeeLocations')}
              </LinkWrapper>
            )}
          </div>
        </Column>
      </Row>
    );
  }

  renderMainImage(fields) {
    return (
      <Picture
        image={fields.HeroImage1}
        imageSizes={mainImageSizes}
        pictureCss={styles.mainImage}
        instantLoad
      />
    );
  }

  renderLeftImage(fields) {
    return (
      <Picture
        image={fields.HeroImage2}
        imageSizes={leftImageSizes}
        pictureCss={styles.leftImage}
        instantLoad
      />
    );
  }

  safeRender() {
    return (
      <div data-hero css={styles.container}>
        <div css={styles.overflowContainer}>
          <div css={hideElementCss(MediaQueries.lgAndUp)}>
            {this.renderMainImage(this.fields)}
          </div>
          <Container>
            <Row>
              {this.renderContent(this.fields)}
              {this.renderLeftImage(this.fields)}
            </Row>
          </Container>
        </div>
      </div>
    );
  }
}

GeosolutionHeader.displayName = 'GeosolutionHeader';

GeosolutionHeader.defaultProps = {
  rendering: {
    fields: defaultData,
  },
  t: key => key,
  stickyBarContext: {
    showStickyBar() {},
    hideStickyBar() {},
  },
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
  withConsumer(StickyBarConsumer, 'stickyBarContext'),
)(GeosolutionHeader);
